/**
 * THE LIVE NIGHT (2026-10-06, the baked-night follow-up): how much of the night's ambient the hemisphere may give up.
 *
 * THE BAKED NIGHT moves (1 − HEMI_IBL_FRACTION) of the night's ambient (the floor + the town's glow) out of the
 * hemisphere and into the IBL cube. The cube only carries it once a bake that included it LANDS — a staged cycle is ~20
 * frames on a GPU, minutes under SwiftShader. Measured (night-look `followup-gl3`, 2026-10-06): with the hemisphere cut
 * at once and the cube still the old black night, the frame metered 10× darker until the bake landed (and the reverse —
 * the hemisphere restored while the cube still held the night — would over-brighten at dawn). So the hemisphere gives
 * up what the LIVE cube carries, never what the next bake will: the cube's own night irradiance (its baked term, the
 * same quadrature the bake was calibrated with) × the carrier's environment intensity, in lux at this frame's exposure,
 * clamped to the share the hemisphere has to give.
 *
 * Pure (no three): SkyModule computes `liveNightLux` per landed cube, LightingModule calls `nightIblApplied`.
 */
import { nightBakeIrradianceCpu, type NightBakeParams } from 'enginex/render/sky/lut/skyViewNode.js';

type Rgb = { x: number; y: number; z: number };
/** The baked night term as SkyRenderer latches it (SkyBakeNight's shape — scene radiance). */
export interface BakedNightLike {
  zenith: Rgb; horizon: Rgb; ground: Rgb;
  dome: { dirX: number; dirZ: number; halfWidth: number; height: number; color: Rgb };
}

const lum709 = (r: number, g: number, b: number): number => 0.2126 * r + 0.7152 * g + 0.0722 * b;

/** The baked night term's up-facing irradiance (luminance, scene units) — 0 for no term or a non-finite one. */
export function bakedNightIrradianceScene(n: BakedNightLike | null | undefined): number {
  if (!n) return 0;
  const p: NightBakeParams = {
    zenith: [n.zenith.x, n.zenith.y, n.zenith.z],
    horizon: [n.horizon.x, n.horizon.y, n.horizon.z],
    ground: [n.ground.x, n.ground.y, n.ground.z],
    dome: [n.dome.dirX, n.dome.dirZ, n.dome.halfWidth, n.dome.height],
    domeColor: [n.dome.color.x, n.dome.color.y, n.dome.color.z],
  };
  const e = nightBakeIrradianceCpu(p);
  const l = lum709(e[0], e[1], e[2]);
  return Number.isFinite(l) && l > 0 ? l : 0;
}

/** The lux of night ambient the LIVE cube delivers: its baked irradiance × the environment intensity ÷ scene units per lux. */
export function liveNightLux(irradianceScene: number, envIntensity: number, luxToScene: number): number {
  if (!(irradianceScene > 0) || !(envIntensity > 0) || !(luxToScene > 0)) return 0;
  const v = (irradianceScene * envIntensity) / luxToScene;
  return Number.isFinite(v) ? v : 0;
}

/** What the hemisphere gives up: what the live cube carries, never more than the share it has to give (≥ 0). */
export function nightIblApplied(liveLux: number, available: number): number {
  if (!(liveLux > 0) || !(available > 0)) return 0;
  return Math.min(liveLux, available);
}
